import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Strategy, StrategyFormData, FirmType } from '@/types/client';
import { strategiesApi } from '@/services/api';
import FormCard, { FormSection, FormGrid, FormField, FormInput, FormSelect } from '@/components/utils/FormCard';

const StrategyForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    firm_type: 0,
  });

  const [firmTypes, setFirmTypes] = useState<FirmType[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const fetchStrategy = useCallback(async (strategyId: number) => {
    try {
      const strategyData = await strategiesApi.getStrategy(strategyId);
      populateFormWithStrategy(strategyData);
    } catch (err) {
      setError('Failed to fetch strategy details');
      console.error('Error fetching strategy:', err);
    }
  }, []);

  useEffect(() => {
    loadFirmTypes();
    if (isEditing && id) {
      fetchStrategy(parseInt(id));
    }
  }, [id, isEditing, fetchStrategy]);

  const loadFirmTypes = async () => {
    try {
      setDataLoading(true);
      const response = await strategiesApi.getFirmTypes();
      setFirmTypes(response.firm_types);
    } catch (err) {
      setError('Failed to load firm types');
      console.error('Error loading firm types:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const populateFormWithStrategy = (strategyData: Strategy) => {
    setFormData({
      name: strategyData.name,
      code: strategyData.code,
      firm_type: strategyData.firm_type || 0,
    });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) errors.name = 'Strategy name is required';
    if (!formData.code.trim()) errors.code = 'Strategy code is required';
    else if (formData.code.length < 2) errors.code = 'Strategy code must be at least 2 characters';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: name === 'firm_type' ? parseInt(value) || 0 : value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null); // Clear any previous error

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData: StrategyFormData = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        firm_type: formData.firm_type || undefined,
      };

      let savedStrategy: Strategy;

      if (isEditing && id) {
        savedStrategy = await strategiesApi.updateStrategy(parseInt(id), submitData);
      } else {
        savedStrategy = await strategiesApi.createStrategy(submitData);
      }

      navigate(`/strategies/${savedStrategy.id}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail
        || err.response?.data?.code?.[0]
        || err.response?.data?.name?.[0]
        || `Failed to ${isEditing ? 'update' : 'create'} strategy`;
      setError(errorMessage);
      console.error('Error saving strategy:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = loading
    ? 'Saving…'
    : (isEditing ? 'Update Strategy' : 'Create Strategy');

  return (
    <FormCard
      title={isEditing ? 'Edit Strategy' : 'Create New Strategy'}
      subtitle={isEditing
        ? 'Update the strategy details below.'
        : 'Enter the details for the new strategy.'}
      backLabel="← Back to Strategies"
      onBack={() => navigate('/strategies')}
      errorBanner={error || undefined}
      submitLabel={submitLabel}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/strategies')}
      submitting={loading}
      loading={dataLoading}
      loadingMessage="Loading form data…"
      variant="page"
    >
      <FormSection title="Strategy Information">
        <FormGrid>
          <FormField label="Strategy Name" required error={validationErrors.name}>
            <FormInput
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              hasError={Boolean(validationErrors.name)}
              disabled={loading}
              placeholder="Enter strategy name"
            />
          </FormField>

          <FormField label="Strategy Code" required error={validationErrors.code}>
            <FormInput
              type="text"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              hasError={Boolean(validationErrors.code)}
              disabled={loading}
              placeholder="Enter unique strategy code"
              style={{ textTransform: 'uppercase' }}
            />
          </FormField>

          <FormField label="Firm Type">
            <FormSelect
              name="firm_type"
              value={formData.firm_type}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="">Select Firm Type</option>
              {firmTypes.map(firmType => (
                <option key={firmType.value} value={firmType.value}>
                  {firmType.label}
                </option>
              ))}
            </FormSelect>
          </FormField>
        </FormGrid>
      </FormSection>
    </FormCard>
  );
};

export default StrategyForm;
